import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Typography from '@/components/ui/typography';
import { AlertTriangle } from 'lucide-react';
import { AppDispatch, RootState } from '@/store/store';
import { deleteRule, clearDeleteError } from '@/store/rulesSlice';
import { useToastHelpers, TOAST_MESSAGES } from '@/utils/toast';

interface DeleteRuleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleName: string;
  ruleId: string;
  tenantId: string;
  onDeleteSuccess?: () => void;
}

const DeleteRuleModal: React.FC<DeleteRuleModalProps> = ({
  open,
  onOpenChange,
  ruleName,
  ruleId,
  tenantId,
  onDeleteSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { deleteLoading, deleteError } = useSelector(
    (state: RootState) => state.rules,
  );
  const { showSuccess, showError, showInfo } = useToastHelpers();
  const [hasInitiatedDelete, setHasInitiatedDelete] = useState(false);

  // Show success toast when delete is successful
  useEffect(() => {
    if (hasInitiatedDelete && !deleteLoading && !deleteError) {
      showSuccess(TOAST_MESSAGES.SUCCESS_DELETE_RULE);
      onOpenChange(false);
      setHasInitiatedDelete(false);
      // Call the success callback if provided
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    }
  }, [
    hasInitiatedDelete,
    deleteLoading,
    deleteError,
    onOpenChange,
    showSuccess,
    onDeleteSuccess,
  ]);

  // Show error toast when delete fails
  useEffect(() => {
    if (hasInitiatedDelete && !deleteLoading && deleteError) {
      showError(TOAST_MESSAGES.ERROR_DELETE_RULE);
      setHasInitiatedDelete(false);
    }
  }, [hasInitiatedDelete, deleteLoading, deleteError, showError]);

  const handleConfirm = () => {
    dispatch(deleteRule({ ruleId, tenantId }));
    setHasInitiatedDelete(true);
  };

  const handleCancel = () => {
    onOpenChange(false);
    dispatch(clearDeleteError());
    showInfo('Delete cancelled');
  };

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-left">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <Typography
                  variant="h2"
                  weight="semibold"
                  className="text-neutral-900"
                >
                  Delete rule
                </Typography>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <Typography variant="small" className="text-neutral-600">
            Are you sure you want to delete the rule{' '}
            <span className="font-medium text-neutral-900">{ruleName}</span>?
            This action cannot be undone.
          </Typography>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none"
            disabled={deleteLoading}
          >
            Cancel
          </Button>

          <Button
            variant="destructive"
            onClick={handleConfirm}
            className="flex-1 sm:flex-none"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Deleting...' : 'Delete rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteRuleModal;
